import { useState } from 'react';
import { get } from 'lodash';

import { BUSINESS_STRUCTURE, EMPLOYMENT_TYPE, FIELD_PATHS, RELATED_COMPANY_TYPE } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import {
  ABNDetail,
  Company,
  CompanyEntityIndex,
  CompanyOwner,
  EmploymentEntityIndex,
  StoreEmployment,
} from '@funnels/types';

import { ABNSearch, ConfirmDialog } from '../../Common';

const isCompanyLinkedToCurrentRole = (companyClientId: string, applicantId: string, employments: StoreEmployment[]) =>
  employments.some(
    (_, index) =>
      get({ employments }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(index)) === applicantId &&
      get({ employments }, FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(index)) === companyClientId &&
      [EMPLOYMENT_TYPE.PRIMARY, EMPLOYMENT_TYPE.SECONDARY].includes(
        get({ employments }, FIELD_PATHS.EMPLOYMENT_TYPE(index))
      )
  );

const isCompanyUsedByOtherApplicant = (companyClientId: string, applicantId: string, employments: StoreEmployment[]) =>
  employments.some(
    (_, index) =>
      get({ employments }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(index)) !== applicantId &&
      get({ employments }, FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(index)) === companyClientId
  );
interface BusinessABNProps extends CompanyEntityIndex, EmploymentEntityIndex {
  isDisabled?: boolean;
}

export const BusinessABN = (props: BusinessABNProps) => {
  const { employmentIndex, companyIndex, isDisabled } = props;
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [isPromptModalVisible, setIsPromptModalVisible] = useState(false);
  const [promptMessage, setPromptMessage] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [formValues, setFormValue, setFormValues] = useFormValues();
  const applicantId = get(formValues, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(employmentIndex));
  const employments: StoreEmployment[] = get(formValues, FIELD_PATHS.EMPLOYMENTS(), []);
  const employmentStatus = get(formValues, FIELD_PATHS.EMPLOYMENT_TYPE(employmentIndex));
  const companies: Company[] = get(formValues, FIELD_PATHS.COMPANIES(), []);
  const companyClientId = get(formValues, FIELD_PATHS.COMPANY_CLIENT_ID(companyIndex));
  const abn = get(formValues, FIELD_PATHS.COMPANY_ABN(companyIndex));
  const businessName = get(formValues, FIELD_PATHS.COMPANY_BUSINESS_NAME(companyIndex), '');
  const businessType = get(formValues, FIELD_PATHS.COMPANY_TYPE(companyIndex));
  const privateOrPublic = get(formValues, FIELD_PATHS.COMPANY_PRIVATE_OR_PUBLIC(companyIndex));
  const gstRegisteredDate = get(formValues, FIELD_PATHS.COMPANY_GST_REGISTERED_DATE(companyIndex));
  const abnVerified = get(formValues, FIELD_PATHS.COMPANY_ABN_VERIFIED(companyIndex));
  // remember the current abn details in case the user failed to update, so the previous details can be retrieved
  const [lastABNDetails, setLastABNDetails] = useState({
    abn,
    privateOrPublic,
    gstRegisteredDate,
    abnVerified,
  });

  const handleSelectABN = (businessDetails: ABNDetail) => {
    // find if there is a company with the selected abn
    const company = companies.find(
      (_, index) =>
        get({ companies }, FIELD_PATHS.COMPANY_ABN(index)) === Number(businessDetails.abn) &&
        get({ companies }, FIELD_PATHS.COMPANY_TYPE(index)) === businessType &&
        get({ companies }, FIELD_PATHS.COMPANY_CLIENT_ID(index)) !== companyClientId
    );
    if (businessType === RELATED_COMPANY_TYPE.SELF_EMPLOYMENT_BUSINESS && company) {
      const foundCompanyId = get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0));
      const foundCompanyName = get({ companies: [company] }, FIELD_PATHS.COMPANY_BUSINESS_NAME(0));
      if (
        [EMPLOYMENT_TYPE.PRIMARY, EMPLOYMENT_TYPE.SECONDARY].includes(employmentStatus) &&
        isCompanyLinkedToCurrentRole(foundCompanyId, applicantId, employments)
      ) {
        setIsPromptModalVisible(true);
        setPromptMessage(
          `${foundCompanyName} has already been entered as a primary or secondary role for this applicant.`
        );
      } else if (
        businessDetails.businessType.toUpperCase() === BUSINESS_STRUCTURE.SOLE_TRADER &&
        isCompanyUsedByOtherApplicant(foundCompanyId, applicantId, employments)
      ) {
        setIsPromptModalVisible(true);
        setPromptMessage(`A sole trader business cannot be shared by multiple applicants.`);
      } else {
        setSelectedCompanyId(foundCompanyId);
        setIsConfirmModalVisible(true);
      }
    } else {
      if (
        businessType === RELATED_COMPANY_TYPE.SELF_EMPLOYMENT_BUSINESS &&
        businessDetails.businessType.toUpperCase() === BUSINESS_STRUCTURE.SOLE_TRADER &&
        isCompanyUsedByOtherApplicant(companyClientId, applicantId, employments)
      ) {
        setIsPromptModalVisible(true);
        setPromptMessage(`A sole trader business cannot be shared by multiple applicants.`);
        setFormValues({
          [FIELD_PATHS.COMPANY_ABN(companyIndex)]: lastABNDetails.abn,
          [FIELD_PATHS.COMPANY_PRIVATE_OR_PUBLIC(companyIndex)]: lastABNDetails.privateOrPublic,
          [FIELD_PATHS.COMPANY_GST_REGISTERED_DATE(companyIndex)]: lastABNDetails.gstRegisteredDate,
          [FIELD_PATHS.COMPANY_ABN_VERIFIED(companyIndex)]: lastABNDetails.abnVerified,
        });
      } else {
        setFormValues({
          [FIELD_PATHS.COMPANY_ABN(companyIndex)]: Number(businessDetails.abn),
          [FIELD_PATHS.COMPANY_BUSINESS_NAME(companyIndex)]: businessDetails.entityName ?? businessName,
          [FIELD_PATHS.COMPANY_BUSINESS_STRUCTURE(companyIndex)]: businessDetails.businessType.toUpperCase(),
          [FIELD_PATHS.COMPANY_PRIVATE_OR_PUBLIC(companyIndex)]: businessDetails.entityType.toUpperCase(),
          [FIELD_PATHS.COMPANY_ABN_VERIFIED(companyIndex)]: businessDetails.abnStatus.toUpperCase() === 'ACTIVE',
          ...(businessType === RELATED_COMPANY_TYPE.SELF_EMPLOYMENT_BUSINESS && {
            [FIELD_PATHS.COMPANY_TRUST_TYPE(companyIndex)]: businessDetails.trustType?.toUpperCase(),
            [FIELD_PATHS.COMPANY_GST_REGISTERED_DATE(companyIndex)]: businessDetails.gstRegisteredDate || undefined,
          }),
          ...(businessType === RELATED_COMPANY_TYPE.SELF_EMPLOYMENT_BUSINESS &&
            businessDetails.businessType.toUpperCase() === BUSINESS_STRUCTURE.SOLE_TRADER && {
              // set ownership percentage to 100% if the business structure is SOLE_TRADER
              [FIELD_PATHS.COMPANY_OWNERS(companyIndex)]: [
                {
                  applicantId,
                  percentOwned: 1,
                },
              ] as CompanyOwner[],
            }),
        });
      }
    }
  };

  const handleUnsetABN = () => {
    setLastABNDetails({
      abn,
      privateOrPublic,
      gstRegisteredDate,
      abnVerified,
    });
    setFormValues({
      [FIELD_PATHS.COMPANY_ABN(companyIndex)]: undefined,
      [FIELD_PATHS.COMPANY_PRIVATE_OR_PUBLIC(companyIndex)]: undefined,
      [FIELD_PATHS.COMPANY_GST_REGISTERED_DATE(companyIndex)]: undefined,
      [FIELD_PATHS.COMPANY_ABN_VERIFIED(companyIndex)]: undefined,
    });
  };

  return (
    <>
      <ABNSearch
        abn={!!abn ? abn.toString() : ''}
        businessName={businessName}
        onABNSelect={handleSelectABN}
        onABNUnset={handleUnsetABN}
        isDisabled={isDisabled}
        fieldPath={FIELD_PATHS.COMPANY_ABN(companyIndex)}
      />
      <ConfirmDialog
        title="The business cannot be selected"
        body={promptMessage}
        isVisible={isPromptModalVisible}
        onCancel={() => setIsPromptModalVisible(false)}
        onConfirm={() => setIsPromptModalVisible(false)}
        confirmButtonText="OK"
        shouldHideCancelButton
      />
      <ConfirmDialog
        title="Update the business information"
        body="You have added an ABN associated with another business on this application. The details and income from the existing business will be used."
        isVisible={isConfirmModalVisible}
        onCancel={() => {
          setIsConfirmModalVisible(false);
          setSelectedCompanyId(null);
        }}
        onConfirm={() => {
          setFormValue(FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(employmentIndex), selectedCompanyId);
          setIsConfirmModalVisible(false);
        }}
        confirmButtonText="UPDATE"
      />
    </>
  );
};
